import React, { useEffect, useState, useRef } from 'react';
import styles from './Navbar.module.css';
import Logo from '../../Assets/Home/new-logo-1.png';
import { Link, useLocation } from 'react-router-dom';
import menuSound from '../../Assets/Audio/link.mp3';

const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Blog', to: '/blog' },
  { label: 'Contact', to: '/contact' },
];

const solutions = [
  { label: 'Digital Media Planning', to: '/digital' },
  { label: 'OOH Media Planning', to: '/oohmedia' },
  { label: 'ATL Media Planning', to: '/atlmedia' },
  { label: 'BTL Media Planning', to: '/btlmedia' },
  { label: 'Transit Media Planning', to: '/transitmedia' },
  { label: 'TVC, Paper & Radio', to: '/tvcmedia' },
  { label: 'Event Planning & Execution', to: '/event' },
  { label: 'Film Production', to: '/filmproduction' },
  { label: 'Offset Designing & Printing', to: '/offsetdesigning' },
  { label: 'Corporate Gifting & Branding', to: '/corporategifting' },
];

const Navbar = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const audioRef = useRef(null);
  const location = useLocation();

  /* Persist theme */
  useEffect(() => {
    const saved = localStorage.getItem('yeskondo-theme');
    if (saved === 'dark') {
      setIsDarkMode(true);
      document.documentElement.setAttribute('data-theme', 'dark');
    }
  }, []);

  useEffect(() => {
    const theme = isDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('yeskondo-theme', theme);
  }, [isDarkMode]);

  /* Scroll shrink */
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* Close menu on route change */
  useEffect(() => {
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  /* Lock body scroll when menu open */
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const toggleTheme = () => setIsDarkMode(prev => !prev);

  const toggleMenu = () => {
    if (!audioRef.current) audioRef.current = new Audio(menuSound);
    audioRef.current.play().catch(() => {});
    setIsMenuOpen(prev => !prev);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
  };

  return (
    <>
      <header className={`${styles.header} ${scrolled ? styles.header_scrolled : ''}`}>
        <div className={styles.navbar}>

          {/* Logo */}
          <Link to='/' className={styles.logo_link} onClick={closeMenu} aria-label="Yeskondo Media Home">
            <img src={Logo} alt='Yeskondo Media — Best Marketing Company in Pune' className={styles.logo} />
          </Link>

          {/* Right side controls */}
          <div className={styles.right_controls}>

            {/* Theme Toggle */}
            <button
              className={styles.theme_btn}
              onClick={toggleTheme}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
            >
              <span className={styles.theme_icon}>
                {isDarkMode ? '☀️' : '🌙'}
              </span>
            </button>

            {/* CTA — hidden on small screens */}
            <Link to='/contact' className={styles.nav_cta}>
              Get a Quote
            </Link>

            {/* Hamburger */}
            <button
              className={`${styles.hamburger} ${isMenuOpen ? styles.hamburger_open : ''}`}
              onClick={toggleMenu}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              <span className={styles.ham_bar}></span>
              <span className={styles.ham_bar}></span>
              <span className={styles.ham_bar}></span>
            </button>
          </div>
        </div>
      </header>

      {/* Full-screen Overlay Menu */}
      <div
        className={`${styles.overlay_menu} ${isMenuOpen ? styles.overlay_open : styles.overlay_closed}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Decorative accent panel */}
        <div className={styles.overlay_accent}></div>

        <div className={styles.overlay_inner}>
          {/* Left — address info */}
          <div className={styles.overlay_left}>
            <p className={styles.overlay_label}>Our Office</p>
            <h2 className={styles.overlay_address}>Deccan, Pune<br />Maharashtra, India</h2>
            <div className={styles.overlay_contact}>
              <a href="tel:+919922966739" className={styles.overlay_phone}>+91 99229 66739</a>
              <a href="mailto:info@yeskondomedia.com" className={styles.overlay_email}>info@yeskondomedia.com</a>
            </div>
          </div>

          {/* Right — nav links */}
          <div className={styles.overlay_right}>
            <nav className={styles.overlay_nav}>
              {navLinks.map((nl, i) => (
                <Link
                  key={i}
                  to={nl.to}
                  className={styles.nav_link}
                  onClick={closeMenu}
                  style={{ '--delay': `${0.05 * i}s` }}
                >
                  {nl.label}
                  <span className={styles.link_arrow}>→</span>
                </Link>
              ))}

              {/* Solutions accordion */}
              <div className={styles.solutions_wrap}>
                <button
                  className={`${styles.nav_link} ${styles.solutions_toggle}`}
                  onClick={() => setIsDropdownOpen(prev => !prev)}
                  aria-expanded={isDropdownOpen}
                  style={{ '--delay': '0.2s' }}
                >
                  Solutions
                  <span className={`${styles.chevron} ${isDropdownOpen ? styles.chevron_open : ''}`}>▾</span>
                </button>

                <div className={`${styles.solutions_grid} ${isDropdownOpen ? styles.solutions_visible : ''}`}>
                  {solutions.map((s, i) => (
                    <Link key={i} to={s.to} className={styles.solution_item} onClick={closeMenu}>
                      {s.label}
                    </Link>
                  ))}
                </div>
              </div>
            </nav>
          </div>
        </div>

        {/* Bottom bar inside menu */}
        <div className={styles.overlay_bottom}>
          <span>© 2024 Yeskondo Media Pvt. Ltd.</span>
          <div className={styles.overlay_social}>
            <a href="https://www.instagram.com/yeskondo_media_pvt_ltd_/" target="_blank" rel="noreferrer" aria-label="Instagram">IG</a>
            <a href="https://www.facebook.com/yeskondomedia" target="_blank" rel="noreferrer" aria-label="Facebook">FB</a>
            <a href="https://www.linkedin.com/company/yeskondo-media-pvt-ltd" target="_blank" rel="noreferrer" aria-label="LinkedIn">LI</a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
